import { s3URL } from '@mathflat/common/index'
import type { AcademyDetail, AcademyWorksheetShareSetting } from '@mathflat/domain/Academy'
import { isCreverseAcademy, isVineAcademy } from '@mathflat/domain/Academy'
import type { ISODateTimeString } from '@mathflat/shared/@types/date'
import { addDays, format, isBefore } from 'date-fns'
import { action, computed, makeObservable, observable } from 'mobx'

import commonRepo from '@/repository/common.repo'
import stringUtils from '@/utils/String/stringUtils'

import { DTO } from '../DTO'

export class Academy extends DTO {
  academyId?: AcademyDetail['academyId']
  status: AcademyDetail['status'] = 'FREE_INPROGRESS'
  name: AcademyDetail['name'] = ''
  email: AcademyDetail['email'] = ''
  logoUrl: AcademyDetail['logoUrl'] = ''
  initialized: AcademyDetail['initialized'] = false
  initializedDate: Date | null = null
  // 학원 대표 번호 (문자 인증 받은) 모든 선생님이 이 번호로만 문자 보낼 수 있음
  cellPhone: AcademyDetail['cellPhone'] = ''
  // 학원 대표 연락처 — 문자 발신번호(cellPhone)와 별개. 미등록 시 빈 문자열.
  repPhone: AcademyDetail['repPhone'] = ''
  sellerCode: AcademyDetail['sellerCode'] = ''
  freeStartDate: AcademyDetail['freeStartDate'] = ''
  freeEndDate: AcademyDetail['freeEndDate'] = ''
  payDate?: AcademyDetail['payDate']
  payStartDate: AcademyDetail['payStartDate'] = ''
  payEndDate: AcademyDetail['payEndDate'] = ''
  freePremiumStartDate: AcademyDetail['freePremiumStartDate'] = ''
  freePremiumEndDate: AcademyDetail['freePremiumEndDate'] = ''
  onlineOnboardingStartedDate: AcademyDetail['onlineOnboardingStartedDate'] = null
  onlineOnboardingEndedDate: AcademyDetail['onlineOnboardingEndedDate'] = null
  logoOperation: AcademyDetail['logoOperation'] = 'REPLACE_DEFAULT'
  logoUrlDateNow? = new Date().getTime()
  worksheetShareSetting: AcademyWorksheetShareSetting = {
    allWorksheetShareEnabled: false,
    allWorksheetShareDatetime: format(new Date(), 'yyyy-MM-ddTHH:mm:ss') as ISODateTimeString,
    details: {
      MY_DB: {
        enabled: false,
        datetime: format(new Date(), 'yyyy-MM-ddTHH:mm:ss') as ISODateTimeString,
      },
    },
  }
  supportedWorkbookTypes: AcademyDetail['supportedWorkbookTypes'] = [
    'CUSTOM',
    'PUBLIC',
    'SCHOOL',
    'ALL',
  ] // 지원교재 리스트 - [CUSTOM: 내 교재, PUBLIC: 시중교재, SCHOOL: 교과서, SCHOOL_TEST: 내신기출 교재, ALL] (필수)
  studentCount = 0
  lessonClassCount = 0
  serviceSubscriptions: AcademyDetail['serviceSubscriptions'] = []
  monthlyBaseFee: AcademyDetail['monthlyBaseFee'] = 0
  customerType: AcademyDetail['customerType'] | null = null

  address: AcademyDetail['address'] = ''
  addressDetail: AcademyDetail['addressDetail'] = ''
  zipCode: AcademyDetail['zipCode'] = ''
  metadata: AcademyDetail['metadata'] = {
    attendanceSmsService: false,
    smsService: false,
    lectureService: false,
    thirdPartyProvisionConsentOnPageShop: false,
    oneClickWorksheetService: true,
    essayAiScoring: true,
    loginServiceByPhoneNumber: true,
    popupTargetingGroup: '',
    freePremiumAvailable: false,
    academyApplicationDate: null,
    academyExpectedCompletionDate: null,
    academyTrainingBCompleted: false,
    academyFinalCompleted: false,
    studentCount: '',
    teacherCount: '',
    curriculums: [],
    subjects: [],
  }
  academyAdditionalData: AcademyDetail['academyAdditionalData'] = {
    salesManager: '',
    authPhoneNumber: '',
    authName: '',
    authBirthday: '',
    sampleSignatureWorkbookShowFlag: true,
    contractContactRequestedFlag: false,
    isSeoltab: false,
    examAutoAssigning: [],
    isBetaTester: false,
    organizationDomainName: null,
    organizationName: null,
  }

  가상학생등록여부 = false
  subscribePlan: AcademyDetail['subscribePlan'] = null
  additionalPricingPlan?: AcademyDetail['additionalPricingPlan']
  authority: AcademyDetail['authority'] = 'BASIC'

  subscribeItems: AcademyDetail['subscribeItems'] = []
  studentLimitCount: AcademyDetail['studentLimitCount'] = 0
  readonly specialContractPlans: AcademyDetail['specialContractPlans'] = []
  signUpFrom: AcademyDetail['signUpFrom'] = null

  availableBetaFeatures: AcademyDetail['availableBetaFeatures'] = []
  languagePackFileUrl: AcademyDetail['languagePackFileUrl'] | null = null
  groupId: AcademyDetail['groupId'] = null
  academyGroupType: AcademyDetail['academyGroupType'] = null
  defaultCustomWorksheetShareScope: AcademyDetail['defaultCustomWorksheetShareScope'] = 'ACADEMY'
  defaultCustomWorkbookShareScope: AcademyDetail['defaultCustomWorkbookShareScope'] = 'ACADEMY'

  constructor(data?: AcademyDetail) {
    super()

    makeObservable<this>(this, {
      status: observable,
      name: observable,
      email: observable,
      logoUrl: observable,
      initialized: observable,
      initializedDate: observable,
      cellPhone: observable,
      repPhone: observable,
      sellerCode: observable,
      freeStartDate: observable,
      freeEndDate: observable,
      onlineOnboardingStartedDate: observable,
      onlineOnboardingEndedDate: observable,
      payDate: observable,
      payStartDate: observable,
      payEndDate: observable,
      monthlyBaseFee: observable,
      customerType: observable,
      logoOperation: observable,
      logoUrlDateNow: observable,
      worksheetShareSetting: observable,
      supportedWorkbookTypes: observable,
      studentCount: observable,
      lessonClassCount: observable,
      가상학생등록여부: observable,
      metadata: observable,
      academyId: observable,
      academyAdditionalData: observable,
      logoUrlAdjusted: computed,
      headerLogoUrl: computed,
      payStatus: computed,
      isNoneLogoUrl: computed,
      isSales: computed,
      isFreeInProgressSales: computed,
      isFreeInProgress: computed,
      needCustomerTypeChange: computed,
      setCustomerType: action,
      subscribePlan: observable,
      additionalPricingPlan: observable,
      authority: observable,
      subscribeItems: observable,
      studentLimitCount: observable,
      specialContractPlans: observable,
      signUpFrom: observable,
      availableBetaFeatures: observable,
      groupId: observable,
      academyGroupType: observable,
      defaultCustomWorksheetShareScope: observable,
      defaultCustomWorkbookShareScope: observable,
      isCreverse: computed,
      isVine: computed,
      serviceSubscriptions: observable,
      isOnlineOnboardingActive: computed,
    })

    if (data) {
      const academyAdditionalData = data.academyAdditionalData ?? {}
      const academyData = {
        ...data,
        _subscribeItems: data.subscribeItems,
        metadata: {
          ...this.metadata,
          ...data.metadata,
          lectureService: data.metadata.lectureService,
          smsService: data.metadata.smsService,
          attendanceSmsService: data.metadata.attendanceSmsService,
          thirdPartyProvisionConsentOnPageShop: data.metadata.thirdPartyProvisionConsentOnPageShop,
          freePremiumAvailable: data.metadata.freePremiumAvailable,
          studentCount: data.metadata.studentCount,
          teacherCount: data.metadata.teacherCount,
          curriculums: data.metadata.curriculums,
          subjects: data.metadata.subjects,
        },
        academyAdditionalData: {
          ...this.academyAdditionalData,
          ...academyAdditionalData,
        },
        serviceSubscriptions: data.serviceSubscriptions,
      }

      this.update<Academy>({
        ...academyData,
        initializedDate: academyData.initializedDate ? new Date(academyData.initializedDate) : null,
      })
    }
  }

  get payStatus() {
    switch (this.status) {
      case 'PAY_INPROGRESS':
        return '정식가입'
      case 'PAY_SCHEDULED':
        return '정식가입 예정'
      case 'FREE_INPROGRESS':
      default:
        return '무료체험'
    }
  }

  get dashedPhone() {
    if (!this.cellPhone) return '-'

    const number = this.cellPhone.match(/\d+/g)?.join('')
    return number ? stringUtils.toDashedPhone(number) : this.cellPhone
  }

  get dashedRepPhone() {
    if (!this.repPhone) return '-'

    const number = this.repPhone.match(/\d+/g)?.join('')
    return number ? stringUtils.toDashedPhone(number) : this.repPhone
  }

  // 이미지 업데이트 후에 캐싱된 값 바라보지 않도록 logoUrl뒤에 난수 추가. 업데이트 하지 않을 때는 캐시 유지해야하므로 조건부로 붙혀줌.
  get logoUrlAdjusted() {
    return `${this.logoUrl}${this.logoUrlDateNow ? `?${this.logoUrlDateNow}` : ''}`
  }

  /**
   * 헤더 로고 슬롯(112x32) 전용 URL.
   *
   * 스쿨플랫 기본 로고 PNG(540x216)는 비율이 달라 좌우 여백이 남아서, 기관이 로고를 올리지 않은
   * 경우에만 슬롯 비율에 맞춘 SVG 로 바꾼다. logoUrl 자체를 덮으면 학습지 렌더·HWP 미리보기·
   * 표지 에디터까지 SVG 로 바뀌므로(표지는 이 값을 파일로 업로드한다) 헤더에서만 갈아끼운다.
   */
  get headerLogoUrl() {
    if (commonRepo.isSchoolflat && this.logoOperation === 'REPLACE_DEFAULT') {
      return s3URL.mathflatAssetsBucket.teacher('images/logos/schoolflat_logo.svg')
    }

    return this.logoUrlAdjusted
  }

  get isNoneLogoUrl() {
    return this.logoUrl.includes('None.png')
  }

  get isSales() {
    return this.sellerCode && this.sellerCode.startsWith('SYS')
  }

  get isFreeInProgressSales() {
    return this.isFreeInProgress && this.isSales
  }

  get isB2G() {
    return this.customerType?.includes('B2G')
  }

  get isFranchise() {
    return this.customerType === 'B2B-프랜차이즈'
  }

  get isCreverse() {
    return isCreverseAcademy(this.academyGroupType)
  }

  get isVine() {
    return isVineAcademy(this.academyGroupType)
  }

  get isFreeInProgress() {
    return this.status === 'FREE_INPROGRESS'
  }

  get isOnlineOnboardingActive() {
    if (!this.onlineOnboardingStartedDate || !this.onlineOnboardingEndedDate) return false
    const today = format(new Date(), 'yyyy-MM-dd')
    return this.onlineOnboardingStartedDate <= today && today <= this.onlineOnboardingEndedDate
  }

  get isScienceflatAllowed() {
    return this.serviceSubscriptions.includes('SCIENCEFLAT')
  }

  get isEnglishflatAllowed() {
    return this.serviceSubscriptions.includes('ENGLISHFLAT')
  }

  get needCustomerTypeChange() {
    const targetTypes = ['전화 분류 필요', '분류 필요']

    return (
      this.status === 'PAY_INPROGRESS' &&
      (!this.customerType || targetTypes.includes(this.customerType))
    )
  }

  get subscriptionPlanType() {
    switch (this.subscribePlan) {
      case 'PREMIUM':
      case 'PREMIUM_PLUS':
      case 'PREMIUM_CUSTOM':
      case 'PREMIUM_UNLIMITED':
      case 'PREMIUM_STUDENT_CONTRACT':
        return '프리미엄요금제' // 프리미엄 요금제는 항상 정식가입/정식예정이며 authority도 PREMIUM 이다.
      default:
        return '베이직요금제'
    }
  }

  // B2G 는 계약 요금제(subscribePlan)가 정본이라 그대로 본다.
  get isPremiumPlan() {
    return this.isB2G
      ? this.subscriptionPlanType === '프리미엄요금제'
      : this.authority === 'PREMIUM'
  }

  get isStudentContractPlan() {
    return this.subscribePlan === 'PREMIUM_STUDENT_CONTRACT'
  }

  get isPayStartDateIn90Days() {
    if (!this.payStartDate) return false

    return isBefore(new Date(), addDays(new Date(this.payStartDate), 90))
  }

  // 시작일·종료일 당일 포함. Date 파싱 시 종료일이 자정 기준이 되어 당일 대부분이 제외되는
  // 문제를 피하려고 yyyy-MM-dd 문자열 비교로 판단한다.
  // computed 로 등록하지 않는다 — new Date() 는 observable 이 아니라 computed 로 두면 첫 평가
  // 시점의 today 가 메모이즈돼, SPA 를 켜둔 채 기간 경계를 넘어도 판정이 갱신되지 않는다.
  // 일반 getter 로 두어 접근(렌더)마다 현재 시각으로 재평가되게 한다.
  get isWithinOnlineOnboardingPeriod() {
    if (!this.onlineOnboardingStartedDate || !this.onlineOnboardingEndedDate) return false

    const today = format(new Date(), 'yyyy-MM-dd')
    return this.onlineOnboardingStartedDate <= today && today <= this.onlineOnboardingEndedDate
  }

  setCustomerType(type: AcademyDetail['customerType'] | null) {
    this.customerType = type
  }
}
